import {
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dropdown,
  Grid,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  styled,
  Typography,
} from '@mui/joy';
import CalyxLogo from 'jsx:../../assets/icons/logo.svg';
import CalyxLogoDark from 'jsx:../../assets/icons/logoDark.svg';
import { BiLock } from 'react-icons/bi';
import { IoChevronDown } from 'react-icons/io5';
import { IoMenu } from 'react-icons/io5';
import { Link } from 'react-router-dom';

import { track } from '../../services/analytics';
import { EVENT_DASHBOARD_PAGE, EVNTAPP_signout_menu_click } from '../../services/analytics/events';

import { DesktopProfileMenu } from './DesktopProfileMenu';
import { useNavbarState } from './hooks/useNavbarState';
import { MobileDrawer } from './MobileDrawer';

const FOLLOWING_PROJECTS_ON_SEARCH_PAGE = '/credit-ratings?following=true';

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  borderRadius: theme.radius.xl,
  height: '44px',
  padding: 1,
  border: `1px solid transparent`,
  '&:hover': {
    backgroundColor: theme.palette.primary.outlinedHoverBg,
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledNavBox = styled(Box)<{ active?: boolean }>(({ theme, active }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(1)} ${theme.spacing(3)}`,
  backgroundColor: active ? theme.palette.primary.outlinedHoverBg : '',
}));

const StyledAccordionSummary = styled(AccordionSummary)({
  background: 'transparent !important',
  ':active': {
    border: 'none',
  },
  '>button': {
    background: 'transparent !important',
    justifyContent: 'start',
  },
});

const Navbar = () => {
  const {
    isFreemiumUser,
    isVersionMismatch,
    isDemoUser,
    userInfo,
    isCalyxTeamMember,
    isMobile,
    mode,
    isHoveringOverInsights,
    setIsHoveringOverInsights,
    isHoveringOverProfile,
    setIsHoveringOverProfile,
    isDrawerOpen,
    setIsDrawerOpen,
    navItemsConfig,
    profileMenuConfig,
    themeOptionsList,
    envOptionsList,
    appStateData,
    filterState,
    followingProjectsCount,
    isLoading,
    location,
    logout,
    handleNavItemClick,
    handleThemeSwitch,
    handleEnvToggle,
    handleUpgradeClick,
    handleFollowingClick,
  } = useNavbarState();

  return (
    <Stack
      sx={{
        position: 'fixed',
        width: 1,
        top: 0,
        zIndex: 11,
        background: (theme) => theme.palette.background.surface,
        borderBottom: (theme) => `1px solid ${theme.palette.primary.outlinedBorder}`,
      }}
    >
      {isVersionMismatch && (
        <Alert variant='soft' color='warning' sx={{ py: 0 }}>
          <Typography fontSize={'sm'} fontWeight={'lg'} mx={'auto'}>
            Your app version is out of date. Please{' '}
            <Link to='#' onClick={() => window.location.reload()}>
              refresh
            </Link>{' '}
            to load the latest version.
          </Typography>
        </Alert>
      )}
      <MobileDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        navItemsConfig={navItemsConfig}
        profileMenuConfig={profileMenuConfig}
        themeOptionsList={themeOptionsList}
        envOptionsList={envOptionsList}
        mode={mode}
        isCalyxTeamMember={isCalyxTeamMember}
        appIsStaging={appStateData.isStaging}
        filterState={filterState}
        followingProjectsCount={followingProjectsCount}
        isLoading={isLoading}
        locationPathname={location.pathname}
        handleNavItemClick={handleNavItemClick}
        handleThemeSwitch={handleThemeSwitch}
        handleEnvToggle={handleEnvToggle}
        handleFollowingClick={handleFollowingClick}
        logout={logout}
        StyledNavBox={StyledNavBox}
        StyledAccordionSummary={StyledAccordionSummary}
        StyledLink={StyledLink}
        analyticsTrack={track}
        signoutEvent={EVNTAPP_signout_menu_click}
        dashboardPage={EVENT_DASHBOARD_PAGE}
      />
      <Grid container justifyContent={'space-between'} alignItems={'center'} padding={'4px 24px'}>
        <Stack alignItems={'center'} direction={'row'} gap={3}>
          <Link to={'/'}>
            {mode === 'light' ? (
              <CalyxLogo width={164} height={36} />
            ) : (
              <CalyxLogoDark width={164} height={36} />
            )}
          </Link>
          <Grid
            container
            spacing={2}
            alignItems={'center'}
            sx={{ display: { xs: 'none', lg: 'flex' } }}
          >
            {navItemsConfig.map(
              ({
                path,
                title,
                isNew,
                isDropdown = false,
                items = [],
                isBeta = false,
                event,
                show,
                activeCallback,
                hasPermission = true,
              }) => {
                const isActive =
                  (isDropdown
                    ? items?.some(({ path }) => path === location.pathname)
                    : path === location.pathname || location.pathname.startsWith(path)) ||
                  activeCallback?.(location.pathname);

                if (!show) {
                  return null;
                }
                return isDropdown ? (
                  <Grid
                    padding={1}
                    sx={{
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => {
                      setIsHoveringOverInsights(true);
                    }}
                    onMouseLeave={() => {
                      setIsHoveringOverInsights(false);
                    }}
                  >
                    <Dropdown>
                      <Stack mt={'4px'}>
                        <MenuButton variant='plain' color='primary' sx={{ p: 0 }}>
                          <Typography
                            fontSize={'md'}
                            fontWeight={'md'}
                            sx={{ color: (theme) => theme.palette.primary.plainColor }}
                            endDecorator={<IoChevronDown />}
                          >
                            {title}
                          </Typography>
                        </MenuButton>
                        {isActive ? (
                          <Box
                            width={'100%'}
                            position={'relative'}
                            top={10}
                            height={'4px'}
                            sx={(theme) => {
                              return { background: theme.palette.primary.solidBg };
                            }}
                          />
                        ) : (
                          <Box height={'4px'} />
                        )}
                      </Stack>
                      <Menu open={isHoveringOverInsights} sx={{ maxWidth: 330, p: 2 }}>
                        {items.map(
                          ({
                            path,
                            title,
                            subtitle,
                            event: childEvent,
                            isNew,
                            isBeta,
                            hasPermission: childHasPermission = true,
                          }) => {
                            return (
                              <Link
                                to={path}
                                style={{ textDecoration: 'none' }}
                                onClick={() => handleNavItemClick(childEvent)}
                              >
                                <MenuItem sx={{ borderRadius: '4px' }}>
                                  <Stack gap={1} p={'12px'}>
                                    <Typography
                                      fontSize={'lg'}
                                      fontWeight={'md'}
                                      lineHeight={'24px'}
                                      color='primary'
                                      startDecorator={childHasPermission ? null : <BiLock />}
                                      endDecorator={
                                        isNew || isBeta ? (
                                          <>
                                            {isNew ? (
                                              <Chip variant='solid' color='primary'>
                                                New
                                              </Chip>
                                            ) : null}
                                            {isBeta ? (
                                              <Chip variant='solid' color='primary'>
                                                Beta
                                              </Chip>
                                            ) : null}
                                          </>
                                        ) : null
                                      }
                                    >
                                      {title}
                                    </Typography>
                                    <Typography
                                      fontSize={'sm'}
                                      fontWeight={'md'}
                                      lineHeight={'16px'}
                                      sx={{ color: (theme) => theme.palette.text.secondary }}
                                    >
                                      {subtitle}
                                    </Typography>
                                  </Stack>
                                </MenuItem>
                              </Link>
                            );
                          }
                        )}
                      </Menu>
                    </Dropdown>
                  </Grid>
                ) : (
                  <StyledLink
                    to={path}
                    style={{ textDecoration: 'none' }}
                    onClick={() => handleNavItemClick(event)}
                  >
                    <Stack>
                      <Grid padding={1} sx={{ cursor: 'pointer' }} mt={'4px'}>
                        <Typography
                          fontSize={'md'}
                          fontWeight={'md'}
                          color='primary'
                          startDecorator={hasPermission ? null : <BiLock />}
                          endDecorator={
                            isNew || isBeta ? (
                              <>
                                {isNew ? (
                                  <Chip variant='solid' color='primary'>
                                    New
                                  </Chip>
                                ) : null}
                                {isBeta ? (
                                  <Chip variant='solid' color='primary'>
                                    Beta
                                  </Chip>
                                ) : null}
                              </>
                            ) : null
                          }
                        >
                          {title}
                        </Typography>
                      </Grid>
                      {isActive ? (
                        <Box
                          width={'100%'}
                          position={'relative'}
                          top={8}
                          height={'4px'}
                          sx={(theme) => {
                            return { background: theme.palette.primary.solidBg };
                          }}
                        />
                      ) : (
                        <Box height={'4px'} />
                      )}
                    </Stack>
                  </StyledLink>
                );
              }
            )}
          </Grid>
        </Stack>
        <Stack alignItems={'center'} gap={1} direction={'row'}>
          {(isFreemiumUser || isDemoUser) && (
            <Button variant='solid' color='primary' size='sm' onClick={handleUpgradeClick}>
              Upgrade
            </Button>
          )}
          {isMobile ? null : (
            <DesktopProfileMenu
              isHoveringOverProfile={isHoveringOverProfile}
              setIsHoveringOverProfile={setIsHoveringOverProfile}
              userInfo={userInfo}
              themeOptionsList={themeOptionsList}
              envOptionsList={envOptionsList}
              mode={mode}
              isCalyxTeamMember={isCalyxTeamMember}
              appIsStaging={appStateData.isStaging}
              filterState={filterState}
              followingProjectsCount={followingProjectsCount}
              isLoading={isLoading}
              profileMenuConfig={profileMenuConfig}
              followingUrl={`${FOLLOWING_PROJECTS_ON_SEARCH_PAGE}${filterState?.searchParams.has('view') ? `&view=${filterState?.searchParams.get('view')}` : ''}`}
              handleThemeSwitch={handleThemeSwitch}
              handleEnvToggle={handleEnvToggle}
              handleFollowingClick={handleFollowingClick}
              StyledLink={StyledLink}
            />
          )}
          <IconButton
            variant='plain'
            onClick={() => setIsDrawerOpen(true)}
            sx={{ display: { lg: 'none' } }}
          >
            <IoMenu fontSize={20} />
          </IconButton>
        </Stack>
      </Grid>
    </Stack>
  );
};

export { Navbar };
